/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiFieldText,
  EuiFormRow,
  EuiCheckbox,
  EuiSpacer,
  EuiText,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

/* ==========================================================================
   CONSTANTS & DOMAIN TYPES
   ========================================================================== */

export interface GcpState {
  /** The alphanumerical identifier for the Google Cloud Billing Account. */
  billing_account_id: string
  /** Whether to retroactively charge for consumption in the current month. */
  backdate_on_consumption: boolean
}

interface Props {
  values: GcpState
  touched: Partial<Record<keyof GcpState, boolean>>
  isDisabled: boolean
  onChange: (updates: Partial<GcpState>) => void
  onBlur: (field: keyof GcpState) => void
  showBackdate: boolean
}

/* ==========================================================================
   COMPONENT DEFINITION
   ========================================================================== */

const GcpProviderForm = ({
  values,
  touched,
  isDisabled,
  onChange,
  onBlur,
  showBackdate,
}: Props) => {
  // --------------------------------------------------------------------------
  // State & Logic
  // --------------------------------------------------------------------------

  const isInvalid = touched.billing_account_id && !values.billing_account_id.trim()

  // Accessibility: Generate a unique ID for the checkbox label
  const checkboxId = useGeneratedHtmlId({ prefix: 'gcp-backdate' })

  // --------------------------------------------------------------------------
  // Main Render
  // --------------------------------------------------------------------------

  return (
    <React.Fragment>
      {/* SECTION: IDENTITY
        Links the Elastic Organization to the Cloud Service Provider (GCP).
      */}
      <EuiFormRow
        label={<FormattedMessage id='gcp.billing-id' defaultMessage='GCP Billing Account ID' />}
        isInvalid={isInvalid}
        error={
          isInvalid ? (
            <FormattedMessage
              id='gcp.billing-id.is-required'
              defaultMessage='GCP Billing Account ID is required'
            />
          ) : undefined
        }
        helpText={
          <FormattedMessage
            id='change-domain-flyout.billing-account-id.help-text'
            defaultMessage='The GCP Billing Account ID to associate with this organization.'
          />
        }
        fullWidth={true}
      >
        <EuiFieldText
          data-test-id='gcp-billing-account-id-input'
          value={values.billing_account_id}
          icon='tokenAlias'
          onChange={(e) => onChange({ billing_account_id: e.target.value })}
          onBlur={() => onBlur('billing_account_id')}
          readOnly={isDisabled}
          isInvalid={isInvalid}
          placeholder='e.g. 012A34-FB5C67-D8E6F9'
          fullWidth={true}
        />
      </EuiFormRow>

      {showBackdate && (
        <React.Fragment>
          <EuiSpacer size='m' />

          <EuiFormRow
            helpText={
              <FormattedMessage
                id='gcp.backdate_on_consumption.help-text'
                defaultMessage={
                  'Check this only if the customer has EXPLICITLY approved ' +
                  'Elastic to charge outstanding usage accumulated over this ' +
                  'month to their new GCP marketplace subscription. This will ' +
                  'result in an increased one-time consolidated charge on ' +
                  'the initial GCP invoice covering the transition period.'
                }
              />
            }
          >
            <EuiCheckbox
              id={checkboxId}
              label={
                <EuiText size='s'>
                  <strong>
                    <FormattedMessage
                      id='billing.backdate'
                      defaultMessage='Enable retroactive consumption billing'
                    />
                  </strong>
                </EuiText>
              }
              checked={values.backdate_on_consumption}
              onChange={(e) => onChange({ backdate_on_consumption: e.target.checked })}
              disabled={isDisabled}
            />
          </EuiFormRow>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default GcpProviderForm
